import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import styles from './ShowcaseUpload.module.sass';
import { getAuthToken } from '../../utils/authHelpers';

const MAX_ASSETS = 3;
const ALLOWED_IMAGE_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const ALLOWED_IMAGE_EXT = ['jpg', 'jpeg', 'png', 'webp'];
const MAX_IMAGE_SIZE = 15 * 1024 * 1024;

const PROVIDER_LABEL = {
  instagram: 'IG',
  tiktok: 'TikTok',
  youtube: 'YouTube',
};

const generateClientId = () =>
  `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const isLocalId = (id) => typeof id === 'string' && id.startsWith('local-');

const fileExtension = (file) =>
  String(file?.name || '').split('.').pop().toLowerCase();

const validateImageFile = (file) => {
  if (!file) return 'No file selected.';
  const ext = fileExtension(file);
  const mime = (file.type || '').toLowerCase();
  const validMime = ALLOWED_IMAGE_MIME.includes(mime);
  const validExt = ALLOWED_IMAGE_EXT.includes(ext);
  if (!validMime && !validExt) {
    return 'Please upload a JPG, PNG, or WEBP image.';
  }
  if (file.size > MAX_IMAGE_SIZE) {
    return 'Images must be 15MB or smaller.';
  }
  return null;
};

const uploadToCloudinary = (file, signatureData, onProgress) =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', signatureData.apiKey);
    formData.append('timestamp', signatureData.timestamp);
    formData.append('signature', signatureData.signature);
    formData.append('public_id', signatureData.publicId);
    formData.append('eager', signatureData.eager);
    formData.append('overwrite', 'true');
    formData.append('invalidate', 'true');

    const xhr = new XMLHttpRequest();
    const resourceType = signatureData.resourceType || 'image';
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${signatureData.cloudName}/${resourceType}/upload`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText || '{}');
        if (xhr.status >= 200 && xhr.status < 300) resolve(data);
        else reject(new Error(data.error?.message || 'Upload failed.'));
      } catch (error) {
        reject(error);
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed.'));
    xhr.send(formData);
  });

const getActiveEmail = () => {
  if (typeof window === 'undefined') return '';
  try {
    const params = new URLSearchParams(window.location.search || '');
    return params.get('email') || window.localStorage.getItem('postaway_email') || '';
  } catch (_) {
    return '';
  }
};

const requestImageSignature = async () => {
  const token = getAuthToken();
  const email = getActiveEmail();
  const res = await fetch('/api/cloudinary/sign', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ kind: 'showcase_image', email }),
  });
  const data = await res.json();
  if (!res.ok || !data.ok) {
    throw new Error(
      data.error === 'unauthorized'
        ? 'Please sign in before uploading images.'
        : 'Could not prepare upload.'
    );
  }
  return data;
};

const fetchReelThumbnail = async (url) => {
  try {
    const res = await fetch('/api/showcase/reel-thumbnail', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });
    const data = await res.json();
    return data;
  } catch (error) {
    return { ok: false, provider: null };
  }
};

const detectProviderFromUrl = (url) => {
  const u = String(url || '').toLowerCase();
  if (u.includes('instagram.com')) return 'instagram';
  if (u.includes('tiktok.com')) return 'tiktok';
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  return null;
};

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const ImageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const FilmIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
    <line x1="7" y1="2" x2="7" y2="22" />
    <line x1="17" y1="2" x2="17" y2="22" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <line x1="2" y1="7" x2="7" y2="7" />
    <line x1="2" y1="17" x2="7" y2="17" />
    <line x1="17" y1="17" x2="22" y2="17" />
    <line x1="17" y1="7" x2="22" y2="7" />
  </svg>
);

const HandleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="9" cy="6" r="1.5" />
    <circle cx="15" cy="6" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="9" cy="18" r="1.5" />
    <circle cx="15" cy="18" r="1.5" />
  </svg>
);

const ShowcaseUpload = ({
  className,
  mode = 'onboarding', // 'onboarding' | 'dashboard'
  value = [],
  onChange,
  profileKind, // 'creator' | 'host' | 'property' (required for dashboard mode)
  profileId, // required for dashboard mode
  heading,
  copy,
}) => {
  const [picker, setPicker] = useState(null); // { stage: 'choose' | 'reel' | 'reel-thumb', reelUrl, reelProvider }
  const [uploadingSlot, setUploadingSlot] = useState(null); // index being uploaded
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [dragIndex, setDragIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const fileInputRef = useRef(null);
  const fallbackThumbInputRef = useRef(null);

  const assets = useMemo(() => Array.isArray(value) ? value : [], [value]);
  const remaining = Math.max(0, MAX_ASSETS - assets.length);

  const setStatus = useCallback((message, ttl = 2200) => {
    setStatusMessage(message);
    if (message && ttl > 0) {
      setTimeout(() => {
        setStatusMessage((current) => (current === message ? '' : current));
      }, ttl);
    }
  }, []);

  const apiAddAsset = useCallback(async (assetPayload) => {
    if (mode !== 'dashboard') return assetPayload;
    if (!profileId || !profileKind) {
      throw new Error('Profile is not ready yet.');
    }
    const token = getAuthToken();
    const email = getActiveEmail();
    const res = await fetch('/api/showcase', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ profileKind, profileId, asset: assetPayload, email }),
    });
    const data = await res.json();
    if (!res.ok || !data.ok) {
      if (data?.error === 'showcase_limit_reached') {
        throw new Error('You can only add up to 3 showcase assets.');
      }
      throw new Error('Could not save your asset.');
    }
    return data.asset;
  }, [mode, profileKind, profileId]);

  const apiDeleteAsset = useCallback(async (id) => {
    if (mode !== 'dashboard' || isLocalId(id)) return;
    const token = getAuthToken();
    await fetch(`/api/showcase?id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
  }, [mode]);

  const apiReorder = useCallback(async (orderedIds) => {
    if (mode !== 'dashboard') return;
    if (!profileId || !profileKind) return;
    if (orderedIds.some(isLocalId)) return; // Skip API call until rows have been persisted.
    const token = getAuthToken();
    await fetch('/api/showcase/reorder', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ profileKind, profileId, orderedIds }),
    });
  }, [mode, profileKind, profileId]);

  const handleAddAsset = useCallback(async (asset) => {
    setError('');
    if (assets.length >= MAX_ASSETS) {
      setError('You can only add up to 3 showcase assets.');
      return;
    }
    try {
      if (mode === 'dashboard') {
        setStatus('Saving...', 0);
        const saved = await apiAddAsset(asset);
        onChange?.([...assets, saved]);
        setStatus('Saved.');
      } else {
        const local = { ...asset, id: asset.id || generateClientId() };
        onChange?.([...assets, local]);
      }
    } catch (err) {
      setError(err.message || 'Could not save your asset.');
      setStatusMessage('');
    }
  }, [apiAddAsset, assets, mode, onChange, setStatus]);

  const handleDelete = useCallback(async (index) => {
    const asset = assets[index];
    const next = assets.filter((_, i) => i !== index);
    onChange?.(next);
    try {
      if (mode === 'dashboard') {
        setStatus('Removing...', 0);
        await apiDeleteAsset(asset.id);
        setStatus('Removed.');
      }
    } catch (err) {
      setStatusMessage('');
    }
  }, [apiDeleteAsset, assets, mode, onChange, setStatus]);

  const handleImagePick = () => {
    setPicker(null);
    fileInputRef.current?.click();
  };

  const handleImageFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const validationError = validateImageFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (assets.length >= MAX_ASSETS) {
      setError('You can only add up to 3 showcase assets.');
      return;
    }

    setError('');
    setUploadingSlot(assets.length);
    setUploadProgress(0);

    try {
      const signature = await requestImageSignature();
      const uploaded = await uploadToCloudinary(file, signature, setUploadProgress);
      const publicId = uploaded.public_id || signature.publicId;
      const imageUrl = uploaded.secure_url;
      const eager = Array.isArray(uploaded.eager) ? uploaded.eager : [];
      const thumbnailUrl = eager[0]?.secure_url || imageUrl;

      await handleAddAsset({
        assetType: 'image',
        cloudinaryPublicId: publicId,
        imageUrl,
        thumbnailUrl,
      });
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploadingSlot(null);
      setUploadProgress(0);
    }
  };

  const handleStartReel = () => {
    setPicker({ stage: 'reel', reelUrl: '', reelProvider: null });
  };

  const handleResolveReel = async () => {
    const url = (picker?.reelUrl || '').trim();
    if (!url) {
      setError('Please paste a reel URL.');
      return;
    }
    const provider = detectProviderFromUrl(url);
    if (!provider) {
      setError('Reels must be Instagram, TikTok, or YouTube links.');
      return;
    }
    setError('');
    setPicker((prev) => ({ ...prev, stage: 'reel-loading', reelProvider: provider }));

    const data = await fetchReelThumbnail(url);
    if (data?.ok && data.thumbnailUrl) {
      setPicker(null);
      try {
        await handleAddAsset({
          assetType: 'reel',
          reelUrl: url,
          reelProvider: provider,
          thumbnailUrl: data.thumbnailUrl,
        });
      } catch (_) {
        // handleAddAsset already sets error
      }
    } else {
      setPicker({ stage: 'reel-thumb', reelUrl: url, reelProvider: provider });
    }
  };

  const handleReelThumbUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const validationError = validateImageFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    const url = picker?.reelUrl;
    const provider = picker?.reelProvider;
    if (!url || !provider) return;

    setPicker(null);
    setError('');
    setUploadingSlot(assets.length);
    setUploadProgress(0);

    try {
      const signature = await requestImageSignature();
      const uploaded = await uploadToCloudinary(file, signature, setUploadProgress);
      const publicId = uploaded.public_id || signature.publicId;
      const eager = Array.isArray(uploaded.eager) ? uploaded.eager : [];
      const thumbnailUrl = eager[0]?.secure_url || uploaded.secure_url;

      await handleAddAsset({
        assetType: 'reel',
        reelUrl: url,
        reelProvider: provider,
        cloudinaryPublicId: publicId,
        imageUrl: uploaded.secure_url,
        thumbnailUrl,
      });
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploadingSlot(null);
      setUploadProgress(0);
    }
  };

  const handleDragStart = (index) => (event) => {
    setDragIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    try {
      event.dataTransfer.setData('text/plain', String(index));
    } catch (_) {}
  };

  const handleDragOver = (index) => (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dropIndex !== index) setDropIndex(index);
  };

  const handleDragLeave = () => {
    setDropIndex(null);
  };

  const handleDrop = (index) => async (event) => {
    event.preventDefault();
    const from = dragIndex;
    setDragIndex(null);
    setDropIndex(null);
    if (from === null || from === index) return;
    const next = [...assets];
    const [moved] = next.splice(from, 1);
    next.splice(index, 0, moved);
    onChange?.(next);
    try {
      await apiReorder(next.map((a) => a.id));
      if (mode === 'dashboard') setStatus('Reordered.');
    } catch (_) {}
  };

  const slots = useMemo(() => {
    const filled = assets.slice(0, MAX_ASSETS);
    const empty = Math.max(0, MAX_ASSETS - filled.length);
    return [
      ...filled.map((asset) => ({ kind: 'asset', asset })),
      ...Array.from({ length: empty }, () => ({ kind: 'empty' })),
    ];
  }, [assets]);

  return (
    <div className={cn(styles.wrapper, className)}>
      <div className={styles.header}>
        <div>
          <div className={styles.title}>{heading || 'Profile showcase'}</div>
          <div className={styles.copy}>
            {copy || 'Add up to 3 photos or short-form reels that represent your best work.'}
          </div>
        </div>
        <div className={styles.counter}>{assets.length}/{MAX_ASSETS}</div>
      </div>

      <div className={styles.grid}>
        {slots.map((slot, index) => {
          if (slot.kind === 'asset') {
            const { asset } = slot;
            const isReel = asset.asset_type === 'reel' || asset.assetType === 'reel';
            const provider = asset.reel_provider || asset.reelProvider || null;
            const thumb = asset.thumbnail_url || asset.thumbnailUrl || asset.image_url || asset.imageUrl;
            return (
              <div
                key={asset.id || `slot-${index}`}
                className={cn(styles.tile, { [styles.dropTarget]: dropIndex === index })}
                draggable
                onDragStart={handleDragStart(index)}
                onDragOver={handleDragOver(index)}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop(index)}
              >
                {thumb && <img src={thumb} alt="Showcase asset" className={styles.tileImage} />}
                <div className={styles.tileOverlay}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {assets.length > 1 && (
                      <span className={styles.dragHandle} aria-label="Drag to reorder">
                        <HandleIcon />
                      </span>
                    )}
                    {isReel && provider && (
                      <span className={styles.providerBadge}>{PROVIDER_LABEL[provider] || 'REEL'}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={() => handleDelete(index)}
                    aria-label="Remove showcase asset"
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
            );
          }

          const isUploadingSlot = uploadingSlot !== null && index === uploadingSlot;
          return (
            <div
              key={`empty-${index}`}
              className={cn(styles.tile, { [styles.dropTarget]: dropIndex === index })}
              onDragOver={handleDragOver(index)}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop(index)}
            >
              {isUploadingSlot ? (
                <div className={styles.uploading}>Uploading {uploadProgress}%</div>
              ) : (
                <button
                  type="button"
                  className={styles.tileEmpty}
                  onClick={() => setPicker({ stage: 'choose' })}
                  disabled={remaining <= 0}
                >
                  <PlusIcon />
                  <span>Add asset</span>
                  <small>Photo or reel</small>
                </button>
              )}
            </div>
          );
        })}
      </div>

      <input
        ref={fileInputRef}
        className={styles.input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleImageFile}
      />
      <input
        ref={fallbackThumbInputRef}
        className={styles.input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleReelThumbUpload}
      />

      <div className={styles.hint}>
        Photos: JPG, PNG, or WEBP up to 15MB. Reels: paste a public link from Instagram, TikTok, or YouTube.
      </div>
      {error && <div className={styles.error}>{error}</div>}
      {mode === 'dashboard' && statusMessage && <div className={styles.status}>{statusMessage}</div>}

      {picker && createPortal(
        <div className={styles.pickerOverlay} onClick={() => setPicker(null)}>
          <div className={styles.pickerModal} onClick={(e) => e.stopPropagation()}>
            {picker.stage === 'choose' && (
              <>
                <div className={styles.pickerTitle}>Add a showcase asset</div>
                <div className={styles.pickerOptions}>
                  <button type="button" className={styles.pickerOption} onClick={handleImagePick}>
                    <span className={styles.pickerIcon}><ImageIcon /></span>
                    <span>
                      Upload a photo
                      <small>JPG, PNG, or WEBP up to 15MB</small>
                    </span>
                  </button>
                  <button type="button" className={styles.pickerOption} onClick={handleStartReel}>
                    <span className={styles.pickerIcon}><FilmIcon /></span>
                    <span>
                      Add a reel link
                      <small>Instagram, TikTok, or YouTube</small>
                    </span>
                  </button>
                </div>
                <button type="button" className={styles.pickerClose} onClick={() => setPicker(null)}>
                  Cancel
                </button>
              </>
            )}

            {(picker.stage === 'reel' || picker.stage === 'reel-loading') && (
              <>
                <div className={styles.pickerTitle}>Paste your reel link</div>
                <div className={styles.copy}>
                  We&rsquo;ll fetch a thumbnail automatically. The reel itself opens in a new tab when someone clicks the tile.
                </div>
                <div className={styles.reelInputRow}>
                  <input
                    className={styles.reelInput}
                    type="url"
                    placeholder="https://www.instagram.com/reel/..."
                    value={picker.reelUrl}
                    disabled={picker.stage === 'reel-loading'}
                    onChange={(e) => setPicker((prev) => ({ ...prev, reelUrl: e.target.value }))}
                    autoFocus
                  />
                  <div className={styles.reelActions}>
                    <button
                      type="button"
                      className={styles.secondaryButton}
                      onClick={() => setPicker(null)}
                      disabled={picker.stage === 'reel-loading'}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={styles.primaryButton}
                      onClick={handleResolveReel}
                      disabled={picker.stage === 'reel-loading' || !picker.reelUrl}
                    >
                      {picker.stage === 'reel-loading' ? 'Fetching...' : 'Add reel'}
                    </button>
                  </div>
                </div>
              </>
            )}

            {picker.stage === 'reel-thumb' && (
              <>
                <div className={styles.pickerTitle}>Upload a custom thumbnail</div>
                <div className={styles.copy}>
                  We couldn&rsquo;t auto-fetch a thumbnail for that reel. Upload an image to use as the tile.
                </div>
                <div className={styles.reelActions} style={{ marginTop: 14 }}>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => setPicker(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={styles.primaryButton}
                    onClick={() => fallbackThumbInputRef.current?.click()}
                  >
                    Choose image
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ShowcaseUpload;
